import styled from "@emotion/styled";
import { IconButton, Tooltip } from "@mui/material";
import { type PropsWithChildren } from "react";

import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { Icon, SkiliticsIcon } from "../../icons";

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  column-gap: 5px;

  max-width: 100%;

  color: ${({ theme }) => theme.generalTextIcon};
`;

const Content = styled.div`
  flex: 1 1 auto;
  min-width: 0; // Ensures that the content can shrink and not overflow
`;

const DragHandle = styled.div`
  cursor: move;
  color: ${({ theme }) => theme.generalTextIcon};
`;

export type BackgroundTogglePropertiesRowProps = {
  onToggle?: () => void;
  onRemove?: () => void;
  draggable?: boolean;
  toggle?: "on" | "off" | "disabled" | "none";
};

export const ToggleBackgroundPropertiesRow = ({
  onRemove,
  onToggle,
  draggable,
  toggle = "on",
  children,
}: PropsWithChildren<BackgroundTogglePropertiesRowProps>) => {
  const formatMessage = useFormatMessage(formatStrings);

  const title = formatMessage(toggle === "on" ? "hide" : toggle === "off" ? "show" : "disabled");

  return (
    <Row>
      {draggable && (
        <DragHandle>
          <SkiliticsIcon icon="drag-grip" size={12} />
        </DragHandle>
      )}

      <Content>{children}</Content>

      {onRemove && (
        <IconButton onClick={onRemove}>
          <Icon icon="minus" size={14} />
        </IconButton>
      )}

      {toggle !== "none" && (
        <Tooltip title={title} placement="top">
          <div>
            <IconButton disabled={toggle === "disabled"} onClick={onToggle}>
              <SkiliticsIcon icon={toggle === "on" ? "visible-on" : "hidden-off"} size={14} />
            </IconButton>
          </div>
        </Tooltip>
      )}
    </Row>
  );
};

const formatStrings = {
  show: {
    en: "Show",
  },
  hide: {
    en: "Hide",
  },
  disabled: {
    en: "",
  },
} satisfies FormatMessages;
